import { risapi, getAxiosConfig, getAPIFullUrl } from './risapi';

/**
 * Fetches the club fund summary (balance and transaction history).
 * @param {object} filters - Pagination and search filters for the transaction list.
 * @param {function} callback - The function to call with the response.
 */
export const getFundDetails = async (filters, callback) => {
    try {
        // The index route of our apiResource fetches the summary.
        const response = await risapi.get(getAPIFullUrl('/api/fund-contributions', filters), getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};

/**
 * Creates a new fund contribution and its corresponding credit transaction.
 * @param {object} data - The data for the new fund contribution.
 * @param {function} callback - The function to call with the response.
 */
export const createFundContribution = async (data, callback) => {
    try {
        const response = await risapi.post('/api/fund-contributions', data, getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};

/**
 * Updates an existing fund contribution by reversing it and creating a new one.
 * @param {object} data - The updated data for the contribution, including its ID.
 * @param {function} callback - The function to call with the response.
 */
export const updateFundContribution = async (data, callback) => {
    try {
        const response = await risapi.put(`/api/fund-contributions/${data.id}`, data, getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};

/**
 * "Deletes" a fund contribution by creating a reversal (debit) transaction.
 * @param {object} contribution - The contribution object to reverse.
 * @param {function} callback - The function to call with the response.
 */
export const destroyFundContribution = async (contribution, callback) => {
    try {
        const response = await risapi.delete(`/api/fund-contributions/${contribution.id}`, getAxiosConfig());
        if (response.status === 204) {
            callback({ success: true }); // Indicate success
        }
    } catch (error) {
        callback(error);
    }
};


/**
 * Creates a new club fund expense and its corresponding debit transaction.
 */
export const createFundExpense = async (data, callback) => {
    try {
        const response = await risapi.post('/api/club-fund-expenses', data, getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};

/**
 * Updates a club fund expense by reversing the old one and creating a new one.
 * @param {object} data - The updated expense data, including its ID.
 * @param {function} callback - The function to call with the response.
 */
export const updateFundExpense = async (data, callback) => {
    try {
        const response = await risapi.put(`/api/club-fund-expenses/${data.id}`, data, getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};

/**
 * "Deletes" a club fund expense by creating a reversal (credit) transaction.
 * @param {object} expense - The expense object to reverse.
 * @param {function} callback - The function to call with the response.
 */
export const destroyFundExpense = async (expense, callback) => {
    try {
        const response = await risapi.delete(`/api/club-fund-expenses/${expense.id}`, getAxiosConfig());
        if (response.status === 204) {
            callback({ success: true }); // Indicate success
        }
    } catch (error) {
        callback(error);
    }
};


/**
 * Fetches the opening balance transaction for the club's central fund.
 * @param {function} callback The function to call with the response.
 */
export const getFundOpeningBalance = async (callback) => {
    try {
        const response = await risapi.get('/api/financials/fund-opening-balance', getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};

/**
 * Saves or updates the opening balance for the club's central fund.
 * @param {object} data The opening balance data (amount, date, remarks).
 * @param {function} callback The function to call with the response.
 */
export const saveFundOpeningBalance = async (data, callback) => {
    try {
        const response = await risapi.post('/api/financials/fund-opening-balance', data, getAxiosConfig());
        callback(response.data);
    } catch (error) {
        callback(error);
    }
};